/**
 * Army tactics: split a bot's army into SQUADS with proportional force instead of
 * marching everyone blindly at one tile. Each think the army is re-planned:
 *
 *   1. THREATS — enemy soldiers near our army are clustered; each cluster gets a
 *      detachment just big enough to beat it (≈ its combat power, min 1:1). So 2
 *      horse archers harassing us pull ~3 melee to deal with them, not the whole army.
 *   2. OBJECTIVE — the remaining units commit to the best STRIKEABLE target: exposed
 *      economy/production first, then other buildings, and a perimeter WALL only as a
 *      last resort (so a turtled base is besieged, but exposed stuff is hit first).
 *
 * Pure + deterministic (distance, then lower eid) so it's testable and replay-safe.
 */
import type { World } from "../sim/world";
import type { Eid } from "../sim/types";
import type { Ident } from "../sim/components";
import type { Registry } from "../data/registry";
import type { UnitDef } from "../data/schema";
import { findPath } from "../sim/pathfinding";

export interface AttackPlan {
  /** The entity this squad is ordered to attack. */
  target: Eid;
  /** The army units in this squad. */
  units: Eid[];
}

const COMBAT_CLASS = (c?: string): boolean => c === "infantry" || c === "ranged" || c === "cavalry";

/** Which unit a production building should train next, for a BALANCED, COUNTER-AWARE
 *  army. Targets class ratios (≈45% infantry / 30% ranged / 25% cavalry) and shifts
 *  them against the enemy's mix — archer-heavy ⇒ more cavalry (run them down) +
 *  infantry; cavalry-heavy ⇒ more infantry; infantry-heavy ⇒ more ranged. Picks the
 *  class THIS building produces that's furthest BELOW target, then the strongest
 *  AFFORDABLE unit of it (elites first). Null if it trains nothing combat. */
export function chooseUnit(world: World, reg: Registry, player: number, buildingDefId: string): string | null {
  const ps = world.players.get(player);
  if (!ps) return null;
  const roster = reg.unitsOf(ps.civ).filter((u) => u.trainingBuilding === buildingDefId && COMBAT_CLASS(u.class));
  if (roster.length === 0) return null;
  const rosterClasses = new Set(roster.map((u) => u.class));

  // survey own + enemy army by combat class
  const myTeam = world.players.get(player)?.team;
  const mine: Record<string, number> = {}, enemy: Record<string, number> = {};
  for (const [eid, id] of world.ident) {
    if (id.kind !== "unit" || !COMBAT_CLASS(id.cls)) continue;
    const o = world.owner.get(eid)?.player;
    if (o === player) mine[id.cls as string] = (mine[id.cls as string] || 0) + 1;
    else if (o !== undefined && world.players.get(o)?.team !== myTeam) enemy[id.cls as string] = (enemy[id.cls as string] || 0) + 1;
  }

  // desired weights, countering the enemy's dominant class
  let w: Record<string, number> = { infantry: 0.45, ranged: 0.30, cavalry: 0.25 };
  const eTot = (enemy.infantry || 0) + (enemy.ranged || 0) + (enemy.cavalry || 0);
  if (eTot >= 3) {
    const share = (c: string) => (enemy[c] || 0) / eTot;
    if (share("ranged") >= 0.4) w = { infantry: 0.40, ranged: 0.15, cavalry: 0.45 };
    else if (share("cavalry") >= 0.35) w = { infantry: 0.60, ranged: 0.25, cavalry: 0.15 };
    else if (share("infantry") >= 0.5) w = { infantry: 0.30, ranged: 0.50, cavalry: 0.20 };
  }

  // the producible class furthest below its target share
  const myTot = ((mine.infantry || 0) + (mine.ranged || 0) + (mine.cavalry || 0)) || 1;
  let bestCls: string | null = null, bestDeficit = -Infinity;
  for (const c of ["infantry", "ranged", "cavalry"] as const) {
    if (!rosterClasses.has(c)) continue;
    const deficit = (w[c] || 0) - (mine[c] || 0) / myTot;
    if (deficit > bestDeficit) { bestDeficit = deficit; bestCls = c; }
  }
  if (bestCls === null) return null;

  // strongest AFFORDABLE unit of that class (elites first); else the cheapest of it
  const totalCost = (u: UnitDef) => Object.values(u.cost).reduce((s, v) => s + (v ?? 0), 0);
  const afford = (u: UnitDef) => Object.entries(u.cost).every(([res, amt]) => (ps.resources[res] ?? 0) >= (amt ?? 0));
  const ofClass = roster.filter((u) => u.class === bestCls);
  const pool = ofClass.filter(afford);
  const pick = (pool.length ? pool : ofClass).sort((a, b) => totalCost(b) - totalCost(a) || (a.id < b.id ? -1 : 1))[0];
  return pick.id;
}

function isEnemyOf(world: World, player: number, eid: Eid): boolean {
  const o = world.owner.get(eid)?.player;
  if (o === undefined || o === player) return false;
  return world.players.get(o)?.team !== world.players.get(player)?.team;
}

/** Combat weight of a unit — ranged/cavalry count for more (kiters/raiders are
 *  worth extra bodies to put down), so a counter-squad is sized to actually win. */
function unitPower(world: World, eid: Eid): number {
  const cls = world.ident.get(eid)?.cls;
  return cls === "ranged" || cls === "cavalry" ? 1.5 : 1;
}

/** Strategic worth of a target. Economy + production rank high, defensive walls/
 *  gates rank LAST so the army only sieges them when nothing softer is reachable. */
function targetValue(ident: Ident): number {
  if (ident.kind === "unit") {
    if (ident.cls === "worker") return 7;   // economy denial
    if (ident.cls === "priest") return 7;   // soft, force-multiplying healers
    if (ident.cls === "naval") return 0;    // a land army can't reach ships
    return 5;                                // enemy soldiers
  }
  switch (ident.defId) {
    case "town_center": return 9;
    case "barracks": case "stables": return 8; // stop their reinforcements
    case "tower": return 6;
    case "wall": case "gate": return 1;         // last resort
    default: return 4;                          // house / field / storage / temple
  }
}

const d2 = (ax: number, ay: number, bx: number, by: number) => (ax - bx) ** 2 + (ay - by) ** 2;

/** Can the army at (ax,ay) actually STRIKE `eid`? A unit on open ground is
 *  chaseable if a land path exists; a building needs an ADJACENT approach (a town
 *  centre ringed by walls snaps its approach outside the ring → not strikeable, so
 *  the army targets the wall instead). */
function strikeable(world: World, player: number, ax: number, ay: number, eid: Eid, isUnit: boolean): boolean {
  const t = world.transform.get(eid);
  if (!t) return false;
  const radius = world.size * 0.18;
  const path = findPath(world, ax, ay, t.pos.x, t.pos.y, 6000, "land", player, false, radius);
  if (!path || !path.length) return false;
  if (isUnit) return true;
  const end = path[path.length - 1];
  return Math.hypot(end.x - t.pos.x, end.y - t.pos.y) <= world.size * 2.0;
}

interface Foe { eid: Eid; ident: Ident; x: number; y: number; value: number; military: boolean; unit: boolean; }

/** Plan the army into squads: proportional counter-detachments for nearby threats,
 *  the rest on the best objective. Returns one AttackPlan per squad (group of units
 *  → target). Empty when there's no army or no reachable enemy. */
export function planArmy(world: World, player: number, army: Eid[]): AttackPlan[] {
  if (army.length === 0) return [];
  const tile = Math.sqrt(3) * world.size;

  let ax = 0, ay = 0;
  for (const u of army) { const t = world.transform.get(u); if (t) { ax += t.pos.x; ay += t.pos.y; } }
  ax /= army.length; ay /= army.length;

  const foes: Foe[] = [];
  for (const [eid, ident] of world.ident) {
    if (!isEnemyOf(world, player, eid)) continue;
    const tr = world.transform.get(eid);
    if (!tr) continue;
    if (ident.kind === "building") { if (world.building.get(eid)?.isFoundation) continue; }
    else if (ident.kind !== "unit") continue;
    const value = targetValue(ident);
    if (value <= 0) continue;
    foes.push({ eid, ident, x: tr.pos.x, y: tr.pos.y, value, military: ident.kind === "unit" && world.combat.has(eid), unit: ident.kind === "unit" });
  }
  if (foes.length === 0) return [];

  // ---- THREAT CLUSTERS: enemy soldiers within striking distance of our army,
  //      greedily grouped (units within ~3 tiles of each other = one cluster) ----
  const nearMil = foes
    .filter((f) => f.military && d2(f.x, f.y, ax, ay) <= (tile * 10) ** 2)
    .sort((a, b) => d2(a.x, a.y, ax, ay) - d2(b.x, b.y, ax, ay) || a.eid - b.eid);
  const clusters: { anchor: Foe; members: Foe[]; power: number }[] = [];
  for (const m of nearMil) {
    let cl = clusters.find((c) => c.members.some((u) => d2(u.x, u.y, m.x, m.y) <= (tile * 3) ** 2));
    if (!cl) { cl = { anchor: m, members: [], power: 0 }; clusters.push(cl); }
    cl.members.push(m);
    cl.power += unitPower(world, m.eid);
  }

  const free = new Set(army);
  const role = (eid: Eid): "ranged" | "melee" => (world.ident.get(eid)?.cls === "ranged" ? "ranged" : "melee");
  // pick `count` free units near (x,y); when roleOrder is given, prefer those roles
  // first (so melee/cavalry are chosen to CLOSE on walls and chase kiters).
  const takeNearest = (x: number, y: number, count: number, roleOrder?: ("melee" | "ranged")[]): Eid[] => {
    const ranked = [...free].map((u) => ({ u, r: role(u), d: dist2(world, u, x, y) })).sort((p, q) => {
      if (roleOrder) {
        const wp = roleOrder.indexOf(p.r), wq = roleOrder.indexOf(q.r);
        if (wp !== wq) return (wp < 0 ? 9 : wp) - (wq < 0 ? 9 : wq);
      }
      return p.d - q.d || p.u - q.u;
    });
    const picked = ranked.slice(0, count).map((o) => o.u);
    for (const u of picked) free.delete(u);
    return picked;
  };

  const plans: AttackPlan[] = [];

  // ---- proportional counter-squads (MELEE first: cavalry catches kiters / corners
  //      archers), nearest threat first ----
  for (const cl of clusters.sort((a, b) => d2(a.anchor.x, a.anchor.y, ax, ay) - d2(b.anchor.x, b.anchor.y, ax, ay))) {
    const need = Math.max(cl.members.length, Math.ceil(cl.power)); // enough bodies to win
    const squad = takeNearest(cl.anchor.x, cl.anchor.y, need, ["melee", "ranged"]);
    if (squad.length) plans.push({ target: cl.anchor.eid, units: squad });
  }

  // ---- the rest on the best OBJECTIVE, by COMPOSITION ----
  if (free.size > 0) {
    let fx = 0, fy = 0;
    for (const u of free) { const t = world.transform.get(u); if (t) { fx += t.pos.x; fy += t.pos.y; } }
    fx /= free.size; fy /= free.size;
    const objective = pickObjective(world, player, fx, fy, foes);
    if (objective !== null) {
      const objIsBuilding = world.ident.get(objective)?.kind === "building";
      if (!objIsBuilding) {
        plans.push({ target: objective, units: [...free] }); // a unit: everyone piles on
      } else {
        // MELEE/cavalry SIEGE the wall/building (close in, high contact damage,
        // tank tower fire). RANGED hang back and shoot the nearest enemy UNIT — or
        // the building too, only if no unit is reachable (archers vs a tower-defended
        // wall just get picked off).
        const melee = [...free].filter((u) => role(u) === "melee");
        const ranged = [...free].filter((u) => role(u) === "ranged");
        if (melee.length) plans.push({ target: objective, units: melee });
        if (ranged.length) {
          const unit = pickEnemyUnit(world, player, fx, fy, foes);
          plans.push({ target: unit ?? objective, units: ranged });
        }
      }
    } else {
      // nothing strikeable at all — still commit everyone to the nearest foe so the
      // army advances rather than freezing.
      const nearest = foes.slice().sort((a, b) => d2(a.x, a.y, fx, fy) - d2(b.x, b.y, fx, fy) || a.eid - b.eid)[0];
      plans.push({ target: nearest.eid, units: [...free] });
    }
  }

  return plans;
}

/** Nearest STRIKEABLE enemy UNIT from (fx,fy) — what ranged units should shoot
 *  while the melee sieges. Null if no enemy unit is reachable. */
function pickEnemyUnit(world: World, player: number, fx: number, fy: number, foes: Foe[]): Eid | null {
  const units = foes.filter((f) => f.unit).sort((a, b) => d2(a.x, a.y, fx, fy) - d2(b.x, b.y, fx, fy) || a.eid - b.eid);
  let checks = 0;
  for (const f of units) {
    if (checks++ >= 12) break;
    if (strikeable(world, player, fx, fy, f.eid, true)) return f.eid;
  }
  return null;
}

function dist2(world: World, eid: Eid, x: number, y: number): number {
  const t = world.transform.get(eid);
  return t ? d2(t.pos.x, t.pos.y, x, y) : Infinity;
}

/** The best STRIKEABLE objective from (fx,fy): highest value, then nearest. Walls
 *  (value 1) are checked last, so exposed economy/buildings are hit first and a
 *  turtle is only besieged when nothing softer can be reached. */
function pickObjective(world: World, player: number, fx: number, fy: number, foes: Foe[]): Eid | null {
  const ranked = foes.slice().sort((a, b) => b.value - a.value || d2(a.x, a.y, fx, fy) - d2(b.x, b.y, fx, fy) || a.eid - b.eid);
  let checks = 0;
  for (const f of ranked) {
    if (checks++ >= 16) break;
    if (strikeable(world, player, fx, fy, f.eid, f.unit)) return f.eid;
  }
  return null;
}
